/**
 * FaceRegistration 서비스
 * 얼굴 인식 등록 비즈니스 로직
 */

import type { Member, FaceServiceStatus, RegistrationForm } from '../models/FaceRegistration';

export class FaceRegistrationService {
  // 캡처 이미지 검증
  validateCapturedImages(images: string[]): { valid: boolean; message?: string } {
    if (images.length === 0) {
      return { valid: false, message: '최소 1장의 얼굴 사진이 필요합니다.' };
    }

    if (images.length > 10) {
      return { valid: false, message: '최대 10장까지 등록 가능합니다.' };
    }

    return { valid: true };
  }

  // 신규 회원 폼 검증
  validateRegistrationForm(form: RegistrationForm): { valid: boolean; message?: string } {
    if (!form.name.trim()) {
      return { valid: false, message: '이름을 입력해주세요.' };
    }

    if (!form.phone.trim()) {
      return { valid: false, message: '전화번호를 입력해주세요.' };
    }

    const phoneRegex = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
    if (!phoneRegex.test(form.phone.replace(/-/g, ''))) {
      return { valid: false, message: '올바른 전화번호 형식이 아닙니다.' };
    }

    if (form.email && !this.isValidEmail(form.email)) {
      return { valid: false, message: '올바른 이메일 형식이 아닙니다.' };
    }

    return { valid: true };
  }

  // 이메일 유효성 검증
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // 얼굴 인식 서비스 오류 메시지 생성
  formatServiceErrorMessage(status: FaceServiceStatus): string {
    const lightweightPackages = status.lightweight_packages?.join(', ') || 'insightface, onnxruntime, opencv-python';
    const standardPackages = status.standard_packages?.join(', ') || 'face-recognition, dlib, opencv-python';

    return (
      '얼굴 인식 서비스가 설치되어 있지 않습니다.\n\n' +
      '다음 중 하나를 설치해주세요:\n\n' +
      '1. 경량형 (추천):\n' +
      `   pip install ${lightweightPackages}\n\n` +
      '2. 표준형:\n' +
      `   pip install ${standardPackages}`
    );
  }

  // 테스트 결과 메시지 생성
  formatTestResultMessage(success: boolean, matched: boolean, userName?: string, confidence?: number): string {
    if (!success) {
      return '얼굴 인식에 실패했습니다. 다시 시도해주세요.';
    }

    if (matched && userName) {
      const confidenceText = confidence ? ` (신뢰도: ${Math.round(confidence * 100)}%)` : '';
      return `✅ 인식 성공: ${userName}${confidenceText}`;
    }

    return '❌ 등록된 얼굴이 아닙니다.';
  }

  // 캡처 권장 메시지
  getCaptureGuidanceMessage(capturedCount: number): string {
    if (capturedCount === 0) {
      return '정면을 바라보고 촬영해주세요';
    } else if (capturedCount === 1) {
      return '왼쪽을 바라보고 촬영해주세요';
    } else if (capturedCount === 2) {
      return '오른쪽을 바라보고 촬영해주세요';
    } else if (capturedCount < 5) {
      return '다양한 각도에서 추가 촬영해주세요';
    }
    return '충분한 사진이 수집되었습니다';
  }

  // 회원 ID 추출 (여러 필드 지원)
  extractMemberId(member: Member): string {
    return member.user_id || member.member_id || member.id;
  }

  // 회원번호 추출 (여러 필드 지원)
  extractMemberNumber(member: Member): string | undefined {
    return member.member_id || member.memberNumber;
  }
}

export const faceRegistrationService = new FaceRegistrationService();
